import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, CircularProgress, Tab, Tabs, Typography } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import RefreshIcon from '@material-ui/icons/Refresh';
import SyncIcon from '@material-ui/icons/Sync';
import { Alert } from '@material-ui/lab';
import { useDispatch, useSelector } from 'react-redux';
import useToast from '../../../hooks/useToast';
import AddFlagModal from './AddFlagModal';
import AuditDrawer from './AuditDrawer';
import AvailabilityModal from './AvailabilityModal';
import FlagTreeNode from './FlagTreeNode';
import ReleaseModal from './ReleaseModal';
import SyncFlagsModal from './SyncFlagsModal';
import {
  collectEcosystems,
  ecosystemLabel,
  filterTreeByEcosystem,
  treeKeys,
} from './flagDisplay';
import {
  createFeatureFlag,
  getFeatureFlagAudit,
  getFeatureFlagTree,
  syncFeatureFlags,
  updateFlagAvailability,
  updateFlagRelease,
} from './featureFlagsSlice';

/**
 * Feature Management — the control surface for buzzone-api's feature flags.
 *
 * Everything here is a *release* action, not a deploy: each save is a database write that
 * takes effect across every API worker within a second. Nothing here ships code.
 *
 * Requires a BCC principal holding the `featureFlags` scope. Only `bccSuperAdmin` has it
 * today, so a 403 almost certainly means the scope has not been granted to the signed-in
 * role rather than that something is broken.
 */
const FeatureManagement = () => {
  const dispatch = useDispatch();
  const { SuccessToast, ErrorToast, WarningToast } = useToast();

  const { tree, loading, error, audit, auditLoading } = useSelector(
    (state) => state.featureFlags
  );

  const [releaseTarget, setReleaseTarget] = useState(null);
  const [availabilityTarget, setAvailabilityTarget] = useState(null);
  const [auditTarget, setAuditTarget] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [syncOpen, setSyncOpen] = useState(false);
  // Null only until the first tree arrives — one product is always selected after that.
  const [ecosystem, setEcosystem] = useState(null);

  const ecosystems = useMemo(() => collectEcosystems(tree), [tree]);
  const existingKeys = useMemo(() => treeKeys(tree), [tree]);
  const visibleTree = useMemo(
    () => filterTreeByEcosystem(tree, ecosystem),
    [tree, ecosystem]
  );

  const load = () => dispatch(getFeatureFlagTree());

  useEffect(() => {
    load();
  }, []);

  // Settle on a product once the data says which exist. Re-runs if the current selection
  // disappears — a flag can lose its last tag in the catalog, and a tab pointing at
  // nothing would silently show an empty tree.
  useEffect(() => {
    if (!ecosystems.length) {
      if (ecosystem !== null) setEcosystem(null);
      return;
    }
    if (!ecosystem || ecosystems.indexOf(ecosystem) === -1) {
      setEcosystem(ecosystems[0]);
    }
  }, [ecosystems, ecosystem]);

  // Always refetch the whole tree after a write rather than patching state locally. A
  // change to one node alters ancestor gating for everything beneath it, and recomputing
  // that here would duplicate logic the backend owns — and drift from it.
  const afterWrite = (message) => {
    SuccessToast(message);
    setReleaseTarget(null);
    setAvailabilityTarget(null);
    load();
  };

  const submitRelease = (key, data) => {
    setSubmitting(true);
    dispatch(updateFlagRelease({ key, data }))
      .unwrap()
      .then(() => afterWrite(`Release updated for ${key}`))
      .catch((err) => ErrorToast((err && err.message) || `Could not update ${key}`))
      .finally(() => setSubmitting(false));
  };

  const submitAvailability = (key, data) => {
    setSubmitting(true);
    dispatch(updateFlagAvailability({ key, data }))
      .unwrap()
      .then(() => afterWrite(`${key} is now ${data.availability}`))
      .catch((err) => ErrorToast((err && err.message) || `Could not update ${key}`))
      .finally(() => setSubmitting(false));
  };

  /**
   * Row creation, shared by both dialogs.
   *
   * These are the only actions here that can touch another environment, and they carry rows
   * only — never release state. Each dialog owns its own result rendering because the
   * per-environment `releaseStatus` in the response is the point, not a detail: it is what
   * shows that a flag created from staging exists in production and is still off there.
   */
  const runCreate = (payload) => dispatch(createFeatureFlag(payload)).unwrap();
  const runSync = (payload) => dispatch(syncFeatureFlags(payload)).unwrap();

  // A failed peer does not fail the request — the local write committed before the peer was
  // called, so the response is a success containing a divergence. Toast on `peers[].status`,
  // never on the promise resolving.
  const afterRowCreation = (response) => {
    const failed = ((response && response.peers) || []).filter(
      (peer) => peer.status === 'FAILED'
    );
    if (failed.length) {
      WarningToast(
        `Applied here, but ${failed
          .map((peer) => peer.environment)
          .join(', ')} did not — environments have diverged`
      );
    } else {
      SuccessToast('Rows created. Nothing is released yet.');
    }
    load();
  };

  const openAudit = (node) => {
    setAuditTarget(node);
    dispatch(getFeatureFlagAudit(node.node.row.key));
  };

  return (
    <Box>
      <Box display="flex" alignItems="flex-start" mb={1}>
        <Box flex={1}>
          <p style={{ fontSize: '1.5rem', fontWeight: 500, margin: 0 }}>
            Feature Management
          </p>
          <Typography variant="body2" color="textSecondary">
            Turning something on or off here is a release, not a deploy — it takes effect
            across all API workers within a second and is recorded in the release log.
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" style={{ gap: 4 }}>
          <Button startIcon={<AddIcon />} onClick={() => setAddOpen(true)}>
            Add flag
          </Button>
          <Button startIcon={<SyncIcon />} onClick={() => setSyncOpen(true)}>
            Sync catalog
          </Button>
          <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
            Refresh
          </Button>
        </Box>
      </Box>

      <Box mb={2}>
        <Typography variant="caption" color="textSecondary">
          A flag controls <strong>visibility only</strong>. Permissions are enforced
          separately on every request, so switching something on here never grants access
          anyone did not already have.
        </Typography>
      </Box>

      {error && (
        <Box mb={2}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      {loading && !tree.length && (
        <Box display="flex" justifyContent="center" p={6}>
          <CircularProgress />
        </Box>
      )}

      {!loading && !tree.length && !error && (
        <Alert severity="info">
          No feature flags exist in this environment yet. <strong>Sync catalog</strong> creates
          rows for everything <code>flag.catalog.ts</code> declares — preview first. Creating a
          row releases nothing.
        </Alert>
      )}

      {/* One product at a time — there is no "all" tab. Rendered only once a product is
          selected, so MUI never gets a `value` with no matching child. When nothing is
          tagged the info banner below explains why, rather than the control silently
          vanishing and leaving "no products" indistinguishable from "filter broken". */}
      {tree.length > 0 && ecosystems.length > 0 && ecosystem && (
        <Box mb={1} borderBottom="1px solid #eceff1">
          <Tabs
            value={ecosystem}
            onChange={(_e, value) => setEcosystem(value)}
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
            scrollButtons="auto">
            {ecosystems.map((value) => (
              <Tab key={value} label={ecosystemLabel(value)} value={value} />
            ))}
          </Tabs>
        </Box>
      )}

      {tree.length > 0 && ecosystems.length === 0 && (
        <Box mb={2}>
          <Alert severity="info">
            No flag declares a product yet, so there is nothing to filter by. A flag's product
            is set as <code>ecosystem</code> in <code>flag.catalog.ts</code>.
          </Alert>
        </Box>
      )}

      {tree.length > 0 && ecosystem && (
        <Box mb={1}>
          <Typography variant="caption" color="textSecondary">
            Showing {ecosystemLabel(ecosystem)}. Flags with no product set — shared
            infrastructure, and the container nodes that hold the tree together — appear under
            every product, so each tab is a complete view of what affects that product rather
            than only what is exclusive to it. Gating badges refer to the whole tree, so an
            ancestor named as blocking may not be on screen.
          </Typography>
        </Box>
      )}

      {tree.length > 0 && visibleTree.length === 0 && (
        <Alert severity="info">
          No flags belong to {ecosystemLabel(ecosystem)} yet. A flag's product is set in the
          code catalog (<code>flag.catalog.ts</code>), not from this screen.
        </Alert>
      )}

      {visibleTree.length > 0 && (
        <Box border="1px solid #eceff1" borderRadius={4} style={{ overflow: 'hidden' }}>
          {visibleTree.map((node) => (
            <FlagTreeNode
              key={node.key}
              node={node}
              onEditRelease={setReleaseTarget}
              onEditAvailability={setAvailabilityTarget}
              onViewAudit={openAudit}
            />
          ))}
        </Box>
      )}

      <ReleaseModal
        open={Boolean(releaseTarget)}
        node={releaseTarget}
        submitting={submitting}
        onClose={() => setReleaseTarget(null)}
        onSubmit={submitRelease}
      />

      <AvailabilityModal
        open={Boolean(availabilityTarget)}
        node={availabilityTarget}
        submitting={submitting}
        onClose={() => setAvailabilityTarget(null)}
        onSubmit={submitAvailability}
      />

      <AddFlagModal
        open={addOpen}
        existingKeys={existingKeys}
        onDiscover={() => runSync({ dryRun: true, propagate: false })}
        onCreate={runCreate}
        onApplied={afterRowCreation}
        onClose={() => setAddOpen(false)}
      />

      <SyncFlagsModal
        open={syncOpen}
        onSync={runSync}
        onApplied={afterRowCreation}
        onClose={() => setSyncOpen(false)}
      />

      <AuditDrawer
        open={Boolean(auditTarget)}
        node={auditTarget}
        entries={auditTarget ? audit[auditTarget.node.row.key] : []}
        loading={auditLoading}
        onClose={() => setAuditTarget(null)}
      />
    </Box>
  );
};

export default FeatureManagement;
